import { useState } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "../context/AuthContext";
import { setCredentials } from "../store/slices/authSlice";
import {
  useLoginMutation,
  useRegisterMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useForgotPasswordMutation,
  useForgotPasswordVerifyOtpMutation,
  useForgotPasswordResetMutation,
  useForgotPasswordResendOtpMutation,
} from "../store/api/authApi";

function EyeIcon({ show }) {
  return show ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-4 h-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-4 h-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  );
}

function AuthInput({
  icon,
  placeholder,
  type = "text",
  value,
  onChange,
  rightSlot,
}) {
  return (
    <div className="flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus-within:border-primary focus-within:bg-white transition-colors">
      {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="flex-1 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 bg-transparent"
      />
      {rightSlot}
    </div>
  );
}

/* ── Icons ───────────────────────────────────────────── */
const UserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);
const MailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);
const PhoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
    />
  </svg>
);
const LockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
    />
  </svg>
);
// const GoogleIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     className="w-5 h-5"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <path
//       d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
//       fill="#4285F4"
//     />
//     <path
//       d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
//       fill="#34A853"
//     />
//     <path
//       d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
//       fill="#FBBC05"
//     />
//     <path
//       d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
//       fill="#EA4335"
//     />
//   </svg>
// );

/* ── Left panel ──────────────────────────────────────── */
function LeftPanel() {
  return (
    <div
      className="hidden md:flex flex-col justify-end p-8 rounded-l-2xl overflow-hidden relative w-56 lg:w-64 flex-shrink-0"
      style={{
        background:
          "linear-gradient(160deg, #0d5c5c 0%, #0a3d3d 50%, #071e2e 100%)",
      }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute bottom-0 left-0 right-0 h-48 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 120% 60% at 50% 100%, #1a8a8a, transparent)",
          }}
        />
        <div
          className="absolute bottom-10 -left-10 w-48 h-48 rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #2ab8b8, transparent)",
          }}
        />
        <div
          className="absolute top-20 -right-8 w-32 h-32 rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #1a9090, transparent)",
          }}
        />
      </div>
      <p className="relative z-10 font-manrope-bold text-xl text-white leading-snug">
        A booking experience built around your schedule, not ours.
      </p>
    </div>
  );
}

/* ── OTP Input block (shared) ────────────────────────── */
function OtpInputs({ otp, onChange, onKeyDown, error }) {
  return (
    <div className="flex justify-center gap-1.5 sm:gap-2">
      {otp.map((digit, i) => (
        <input
          key={i}
          id={`otp-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => onChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          className={`w-10 h-12 sm:w-11 flex-shrink-0 text-center border rounded-xl font-manrope-bold text-lg outline-none transition-colors
            ${error ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-primary bg-gray-50 focus:bg-white"}`}
        />
      ))}
    </div>
  );
}

/* ── Sign Up view ────────────────────────────────────── */
function SignUpView() {
  const { setModalView } = useAuth();
  const [register, { isLoading }] = useRegisterMutation();
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    password: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [alreadyExists, setAlreadyExists] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError("");
    setAlreadyExists(false);
    if (!form.name || !form.email || !form.password) {
      setError("Please fill in all required fields.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    try {
      await register({
        name: form.name,
        email: form.email,
        phone: form.whatsapp,
        password: form.password,
      }).unwrap();
      setModalView({ view: "verify-otp-signup", email: form.email });
    } catch (err) {
      if (err?.status === 409) {
        setAlreadyExists(true);
      } else {
        setError(
          err?.data?.message || "Registration failed. Please try again."
        );
      }
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1 text-center">
        <h2 className="font-manrope-bold text-2xl text-black">
          Sign up your account
        </h2>
        <p className="font-manrope-regular text-sm text-secondary">
          Fill up all the necessary details
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <AuthInput
          icon={<UserIcon />}
          placeholder="Your full name"
          value={form.name}
          onChange={set("name")}
        />
        <AuthInput
          icon={<MailIcon />}
          placeholder="Email address"
          type="email"
          value={form.email}
          onChange={set("email")}
        />
        <AuthInput
          icon={<PhoneIcon />}
          placeholder="Whatsapp number (optional)"
          type="tel"
          value={form.whatsapp}
          onChange={set("whatsapp")}
        />
        <AuthInput
          icon={<LockIcon />}
          placeholder="Enter your password"
          type={showPass ? "text" : "password"}
          value={form.password}
          onChange={set("password")}
          rightSlot={
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="text-gray-400 hover:text-gray-600"
            >
              <EyeIcon show={showPass} />
            </button>
          }
        />
        <AuthInput
          icon={<LockIcon />}
          placeholder="Confirm your password"
          type={showConfirm ? "text" : "password"}
          value={form.confirm}
          onChange={set("confirm")}
          rightSlot={
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="text-gray-400 hover:text-gray-600"
            >
              <EyeIcon show={showConfirm} />
            </button>
          }
        />
      </div>

      {error && (
        <p className="text-xs font-manrope-regular text-red-500 text-center">
          {error}
        </p>
      )}
      {alreadyExists && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
          <p className="font-manrope-medium text-sm text-amber-800">
            This email is already registered.{" "}
            <button
              onClick={() => setModalView("sign-in")}
              className="font-manrope-bold text-primary hover:opacity-80 transition-opacity"
            >
              Sign in instead ▶
            </button>
          </p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {isLoading ? "Creating account…" : "Sign up"}
      </button>

      {/* <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="font-manrope-regular text-sm text-secondary">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <button className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-3 font-manrope-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors">
        <GoogleIcon /> Continue with google
      </button> */}

      <p className="text-center font-manrope-regular text-sm text-secondary">
        Already have an account?{" "}
        <button
          onClick={() => setModalView("sign-in")}
          className="font-manrope-semibold text-black hover:text-primary transition-colors"
        >
          Sign in ▶
        </button>
      </p>
    </div>
  );
}

/* ── Verify OTP (sign-up) view ───────────────────────── */
function VerifyOtpSignupView({ email }) {
  const { closeModal, setModalView } = useAuth();
  const dispatch = useDispatch();
  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    setError("");
    if (value && index < 5)
      document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter all 6 digits.");
      return;
    }
    try {
      const res = await verifyOtp({ email, otp: code }).unwrap();
      dispatch(setCredentials({ token: res.data.token, user: res.data.user }));
      closeModal();
    } catch (err) {
      setError(err?.data?.message || "Invalid or expired OTP.");
    }
  };

  const handleResend = async () => {
    try {
      await resendOtp({ email }).unwrap();
      setError("");
    } catch (err) {
      setError(err?.data?.message || "Failed to resend OTP.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1 text-center">
        <h2 className="font-manrope-bold text-2xl text-black">
          Verify your email
        </h2>
        <p className="font-manrope-regular text-sm text-secondary">
          We sent a 6-digit code to {email}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <OtpInputs
          otp={otp}
          onChange={handleOtpChange}
          onKeyDown={handleOtpKeyDown}
          error={!!error}
        />
        {error && (
          <p className="text-center text-xs font-manrope-regular text-red-500">
            {error}
          </p>
        )}
        <p className="text-center text-xs font-manrope-regular text-secondary">
          Didn't receive the code?{" "}
          <button
            onClick={handleResend}
            disabled={isResending}
            className="font-manrope-semibold text-primary hover:opacity-80 disabled:opacity-50"
          >
            {isResending ? "Resending…" : "Resend"}
          </button>
        </p>
      </div>

      <button
        onClick={handleVerify}
        disabled={isLoading}
        className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {isLoading ? "Verifying…" : "Verify OTP"}
      </button>

      <button
        onClick={() => setModalView("sign-up")}
        className="flex items-center justify-center gap-2 font-manrope-medium text-sm text-secondary hover:text-black transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
          />
        </svg>
        Back
      </button>
    </div>
  );
}

/* ── Sign In view ────────────────────────────────────── */
function SignInView() {
  const { closeModal, setModalView } = useAuth();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError("");
    if (!form.email || !form.password) {
      setError("Please enter your email and password.");
      return;
    }
    try {
      const res = await login({
        email: form.email,
        password: form.password,
      }).unwrap();
      dispatch(setCredentials({ token: res.data.token, user: res.data.user }));
      closeModal();
    } catch (err) {
      setError(err?.data?.message || "Invalid email or password.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1 text-center">
        <h2 className="font-manrope-bold text-2xl text-black">
          Sign in to your account
        </h2>
        <p className="font-manrope-regular text-sm text-secondary">
          Welcome back! Enter your details
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <AuthInput
          icon={<MailIcon />}
          placeholder="Email address"
          type="email"
          value={form.email}
          onChange={set("email")}
        />
        <AuthInput
          icon={<LockIcon />}
          placeholder="Enter your password"
          type={showPass ? "text" : "password"}
          value={form.password}
          onChange={set("password")}
          rightSlot={
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="text-gray-400 hover:text-gray-600"
            >
              <EyeIcon show={showPass} />
            </button>
          }
        />
      </div>

      <button
        onClick={() => setModalView("recover")}
        className="text-right font-manrope-medium text-sm text-primary hover:opacity-80 transition-opacity -mt-2"
      >
        Forgot password?
      </button>

      {error && (
        <p className="text-xs font-manrope-regular text-red-500 text-center">
          {error}
        </p>
      )}

      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {isLoading ? "Signing in…" : "Sign in"}
      </button>

      {/* <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="font-manrope-regular text-sm text-secondary">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <button className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-3 font-manrope-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors">
        <GoogleIcon /> Continue with google
      </button> */}

      <p className="text-center font-manrope-regular text-sm text-secondary">
        Don't have an account?{" "}
        <button
          onClick={() => setModalView("sign-up")}
          className="font-manrope-semibold text-black hover:text-primary transition-colors"
        >
          Sign up ▶
        </button>
      </p>
    </div>
  );
}

/* ── Recover Account view ────────────────────────────── */
function RecoverView() {
  const { setModalView } = useAuth();
  const [forgotPassword, { isLoading: isSending }] =
    useForgotPasswordMutation();
  const [forgotPasswordVerifyOtp, { isLoading: isVerifying }] =
    useForgotPasswordVerifyOtpMutation();
  const [forgotPasswordReset, { isLoading: isResetting }] =
    useForgotPasswordResetMutation();
  const [forgotPasswordResendOtp, { isLoading: isResending }] =
    useForgotPasswordResendOtpMutation();

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    setError("");
    if (value && index < 5)
      document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleSendOtp = async () => {
    if (!email) return;
    setError("");
    try {
      await forgotPassword({ email }).unwrap();
      setStep("otp");
    } catch (err) {
      setError(err?.data?.message || "Failed to send OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter all 6 digits.");
      return;
    }
    setError("");
    try {
      const res = await forgotPasswordVerifyOtp({ email, otp: code }).unwrap();
      setResetToken(res.data.resetToken);
      setStep("password");
    } catch (err) {
      setError(err?.data?.message || "Invalid or expired OTP.");
    }
  };

  const handleResendOtp = async () => {
    setError("");
    try {
      await forgotPasswordResendOtp({ email }).unwrap();
    } catch (err) {
      setError(err?.data?.message || "Failed to resend OTP.");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) return;
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError("");
    try {
      await forgotPasswordReset({ resetToken, newPassword }).unwrap();
      setDone(true);
    } catch (err) {
      setError(err?.data?.message || "Failed to reset password.");
    }
  };

  const BackButton = ({ to }) => (
    <button
      onClick={() => (to === "sign-in" ? setModalView("sign-in") : setStep(to))}
      className="flex items-center justify-center gap-2 font-manrope-medium text-sm text-secondary hover:text-black transition-colors"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="w-4 h-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
        />
      </svg>
      {to === "sign-in" ? "Back to sign in" : "Back"}
    </button>
  );

  const steps = ["Email", "Verify OTP", "New password"];
  const stepIndex = step === "email" ? 0 : step === "otp" ? 1 : 2;

  if (done)
    return (
      <div className="flex flex-col gap-6 items-center text-center py-6">
        <div className="w-16 h-16 rounded-full bg-success20 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-8 h-8 text-green-600"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
        <div>
          <h2 className="font-manrope-bold text-2xl text-black">
            Password reset!
          </h2>
          <p className="font-manrope-regular text-sm text-secondary mt-1">
            Your password has been updated successfully.
          </p>
        </div>
        <button
          onClick={() => setModalView("sign-in")}
          className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl hover:opacity-90 transition-opacity"
        >
          Sign in now
        </button>
      </div>
    );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1 text-center">
        <h2 className="font-manrope-bold text-2xl text-black">
          Recover your account
        </h2>
        <p className="font-manrope-regular text-sm text-secondary">
          {step === "email" &&
            "Enter your email to receive a verification code"}
          {step === "otp" && `We sent a 6-digit code to ${email}`}
          {step === "password" && "Create your new password"}
        </p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center justify-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-manrope-bold transition-colors ${
                i <= stepIndex
                  ? "bg-primary text-white"
                  : "bg-gray-200 text-gray-400"
              }`}
            >
              {i < stepIndex ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-3 h-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <span
              className={`text-xs font-manrope-medium hidden sm:block ${i === stepIndex ? "text-black" : "text-gray-400"}`}
            >
              {s}
            </span>
            {i < steps.length - 1 && (
              <div
                className={`w-6 h-px ${i < stepIndex ? "bg-primary" : "bg-gray-200"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step: Email */}
      {step === "email" && (
        <>
          <AuthInput
            icon={<MailIcon />}
            placeholder="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && (
            <p className="text-xs font-manrope-regular text-red-500 text-center">
              {error}
            </p>
          )}
          <button
            onClick={handleSendOtp}
            disabled={!email || isSending}
            className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl transition-opacity disabled:opacity-50 hover:opacity-90"
          >
            {isSending ? "Sending OTP…" : "Send OTP"}
          </button>
          <BackButton to="sign-in" />
        </>
      )}

      {/* Step: OTP */}
      {step === "otp" && (
        <>
          <div className="flex flex-col gap-3">
            <OtpInputs
              otp={otp}
              onChange={handleOtpChange}
              onKeyDown={handleOtpKeyDown}
              error={!!error}
            />
            {error && (
              <p className="text-center text-xs font-manrope-regular text-red-500">
                {error}
              </p>
            )}
            <p className="text-center text-xs font-manrope-regular text-secondary">
              Didn't receive the code?{" "}
              <button
                onClick={handleResendOtp}
                disabled={isResending}
                className="font-manrope-semibold text-primary hover:opacity-80 disabled:opacity-50"
              >
                {isResending ? "Resending…" : "Resend"}
              </button>
            </p>
          </div>
          <button
            onClick={handleVerifyOtp}
            disabled={isVerifying}
            className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {isVerifying ? "Verifying…" : "Verify OTP"}
          </button>
          <BackButton to="email" />
        </>
      )}

      {/* Step: New password */}
      {step === "password" && (
        <>
          <div className="flex flex-col gap-3">
            <AuthInput
              icon={<LockIcon />}
              placeholder="New password"
              type={showPass ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showPass} />
                </button>
              }
            />
            <AuthInput
              icon={<LockIcon />}
              placeholder="Confirm new password"
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showConfirm} />
                </button>
              }
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs font-manrope-regular text-red-500">
                Passwords do not match
              </p>
            )}
            {error && (
              <p className="text-xs font-manrope-regular text-red-500">
                {error}
              </p>
            )}
          </div>
          <button
            onClick={handleResetPassword}
            disabled={
              isResetting || !newPassword || newPassword !== confirmPassword
            }
            className="w-full bg-primary text-white font-manrope-semibold text-base py-3 rounded-xl transition-opacity disabled:opacity-50 hover:opacity-90"
          >
            {isResetting ? "Resetting…" : "Reset password"}
          </button>
          <BackButton to="otp" />
        </>
      )}
    </div>
  );
}

/* ── Modal shell ─────────────────────────────────────── */
export default function AuthModal() {
  const { modalOpen, modalView, closeModal } = useAuth();

  if (!modalOpen) return null;

  const view = typeof modalView === "object" ? modalView.view : modalView;
  const email = typeof modalView === "object" ? modalView.email : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeModal}
      />
      <div className="relative z-10 flex rounded-2xl overflow-hidden shadow-2xl w-full max-w-2xl bg-white max-h-[92vh]">
        <LeftPanel />
        <div className="flex-1 px-5 pt-12 pb-6 sm:p-8 overflow-y-auto max-h-[92vh]">
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {view === "sign-up" && <SignUpView />}
          {view === "sign-in" && <SignInView />}
          {view === "recover" && <RecoverView />}
          {view === "verify-otp-signup" && (
            <VerifyOtpSignupView email={email} />
          )}
        </div>
      </div>
    </div>
  );
}
